import { useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Smartphone, Send, Save, CheckCircle2 } from 'lucide-react';
import RecipientPicker from './RecipientPicker';

const TOKENS = [
  { t: '{{first_name}}', label: 'First Name' },
  { t: '{{last_name}}', label: 'Last Name' },
  { t: '{{full_name}}', label: 'Full Name' },
  { t: '{{company}}', label: 'Company' },
  { t: '{{email}}', label: 'Email' },
];

export default function CampaignEditor({ open, onClose, campaign, templates, meta, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: campaign?.name || '',
    subject: campaign?.subject || '',
    preview_text: campaign?.preview_text || '',
    html_body: campaign?.html_body || '',
    template_id: campaign?.template_id || null,
    audience: campaign?.audience || { crm: { enabled: false, partnership_types: [], statuses: [], tags: [], owners: [], industries: [], sources: [] }, include_newsletter: false, manual: [], excluded_emails: [] },
  }));
  const [busy, setBusy] = useState('');
  const [testTo, setTestTo] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }, { color: [] }, { background: [] }],
        ['image', 'clean'],
      ],
      handlers: {
        image: function () {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            const fd = new FormData();
            fd.append('file', file);
            try {
              const r = await api.post('/comms/upload-image', fd);
              const editor = quillRef.current?.getEditor();
              const range = editor?.getSelection(true);
              editor?.insertEmbed(range?.index || 0, 'image', r.data.url);
              toast.success('Image uploaded');
            } catch { toast.error('Image upload failed'); }
          };
          input.click();
        },
      },
    },
  }), []);

  const insertToken = (t) => {
    const editor = quillRef.current?.getEditor();
    const range = editor?.getSelection(true);
    editor?.insertText(range?.index ?? editor?.getLength() ?? 0, t);
  };

  const applyTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    setForm((f) => ({ ...f, template_id: tplId, subject: f.subject || tpl.subject, html_body: tpl.html_body, preview_text: f.preview_text || tpl.preview_text }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const save = async (thenSubmit = false) => {
    if (!form.name.trim()) { toast.error('Campaign name is required'); return null; }
    setBusy(thenSubmit ? 'submit' : 'save');
    try {
      const r = campaign?.id
        ? await api.put(`/comms/campaigns/${campaign.id}`, form)
        : await api.post('/comms/campaigns', form);
      const saved = r.data;
      if (thenSubmit) {
        await api.post(`/comms/campaigns/${saved.id}/submit`);
        toast.success('Campaign submitted for approval');
      } else {
        toast.success('Draft saved');
      }
      onSaved();
      onClose();
      return saved;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return null;
    } finally { setBusy(''); }
  };

  const sendTest = async () => {
    if (!campaign?.id) {
      toast.error('Save the draft first, then send a test');
      return;
    }
    setBusy('test');
    try {
      await api.put(`/comms/campaigns/${campaign.id}`, form);
      const r = await api.post(`/comms/campaigns/${campaign.id}/send-test`, { to: testTo || undefined });
      toast.success(`Test email sent to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); } finally { setBusy(''); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="campaign-editor">
        <DialogHeader>
          <DialogTitle>{campaign?.id ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Campaign Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. July Member Newsletter" data-testid="campaign-name-input" />
            </div>
            <div>
              <Label className="text-xs">Start from Template</Label>
              <Select value={form.template_id || ''} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="campaign-template-select"><SelectValue placeholder="Choose a template (optional)" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Subject Line</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject — supports {{first_name}}" data-testid="campaign-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Preview Text</Label>
              <Input value={form.preview_text} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} placeholder="Shown after subject in inbox" data-testid="campaign-preview-input" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs">Email Body</Label>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[10px] text-slate-400 uppercase mr-1">Insert:</span>
                {TOKENS.map((tk) => (
                  <button key={tk.t} type="button" onClick={() => insertToken(tk.t)}
                    className="text-[11px] bg-slate-100 hover:bg-slate-200 rounded px-2 py-0.5 text-slate-600" data-testid={`token-${tk.label.toLowerCase().replace(' ', '-')}`}>
                    {tk.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="campaign-body-editor">
              <ReactQuill ref={quillRef} theme="snow" value={form.html_body} onChange={(v) => setForm({ ...form, html_body: v })} modules={modules} style={{ minHeight: 220 }} />
            </div>
            <p className="text-[11px] text-slate-400 mt-1">AAWTA branded header, footer, social links, mailing address & unsubscribe link are added automatically on send.</p>
          </div>

          <div className="border-t pt-4">
            <RecipientPicker audience={form.audience} onChange={(aud) => setForm({ ...form, audience: aud })} />
          </div>

          <div className="border-t pt-4 flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} data-testid="mobile-preview-btn">
              <Smartphone className="w-4 h-4 mr-1.5" /> Mobile Preview
            </Button>
            <div className="flex items-center gap-1.5 ml-auto">
              <Input className="h-9 w-52 text-sm" placeholder="Test email (default: you)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-email-input" />
              <Button variant="outline" size="sm" onClick={sendTest} disabled={!!busy} data-testid="send-test-btn">
                {busy === 'test' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send Test
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => save(false)} disabled={!!busy} data-testid="save-draft-btn">
              {busy === 'save' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save Draft
            </Button>
            <Button className="bg-magenta hover:bg-magenta/90" onClick={() => save(true)} disabled={!!busy} data-testid="submit-approval-btn">
              {busy === 'submit' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              {meta.is_admin ? 'Save & Mark Ready' : 'Submit for Approval'}
            </Button>
          </div>
        </div>

        {showPreview && (
          <Dialog open onOpenChange={() => setShowPreview(false)}>
            <DialogContent className="max-w-md" data-testid="mobile-preview-dialog">
              <DialogHeader><DialogTitle className="text-sm">Mobile Preview (375px)</DialogTitle></DialogHeader>
              <div className="mx-auto border-8 border-slate-800 rounded-[28px] overflow-hidden" style={{ width: 375 }}>
                <iframe title="preview" style={{ width: 375, height: 560, border: 0, background: '#F1F5F9' }}
                  srcDoc={`<div style="font-family:Arial;padding:16px;"><div style="background:#0E1944;color:#fff;padding:14px;border-radius:8px 8px 0 0;font-weight:800;">AAWTA</div><div style="background:#fff;padding:16px;border:1px solid #E2E8F0;font-size:14px;line-height:1.6;">${form.html_body || '<p style="color:#94A3B8;">Your email body…</p>'}</div><div style="background:#F8FAFC;padding:12px;font-size:10px;color:#94A3B8;text-align:center;border-radius:0 0 8px 8px;">AAWTA · 5269 Buford Highway, Suite 213, Doraville, GA 30340 · Unsubscribe</div></div>`} />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
